let csrfToken = '';
let childContextId = '';

export class ApiClientError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
    public readonly details?: unknown
  ) {
    super(message);
  }
}

export function setCsrfToken(value: string): void {
  csrfToken = value;
}

export function setChildContext(value: string): void {
  childContextId = value;
}

export interface ApiOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  setupSecret?: string;
  omitChildContext?: boolean;
}

export async function api<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const method = (options.method ?? 'GET').toUpperCase();
  const headers = new Headers(options.headers);
  if (options.body !== undefined) headers.set('content-type', 'application/json');
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method) && csrfToken) {
    headers.set('x-kidee-csrf', csrfToken);
  }
  if (options.setupSecret) headers.set('x-setup-secret', options.setupSecret);
  if (!options.omitChildContext && childContextId) {
    headers.set('x-kidee-child', childContextId);
  }

  const { omitChildContext: _omitChildContext, ...fetchOptions } = options;
  let response: Response;
  try {
    response = await fetch(`/api/v1${path}`, {
      ...fetchOptions,
      method,
      credentials: 'include',
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body)
    });
  } catch (error) {
    throw new ApiClientError(
      error instanceof Error ? error.message : 'Không thể kết nối máy chủ.',
      0,
      'NETWORK_ERROR'
    );
  }

  if (!response.ok) {
    let payload: { error?: { message?: string; code?: string; details?: unknown } } = {};
    try {
      payload = (await response.json()) as typeof payload;
    } catch {
      // Ignore non-JSON errors.
    }
    throw new ApiClientError(
      payload.error?.message ?? `Yêu cầu thất bại (${response.status}).`,
      response.status,
      payload.error?.code,
      payload.error?.details
    );
  }
  if (response.status === 204) return undefined as T;
  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) return (await response.json()) as T;
  return (await response.text()) as T;
}

export function downloadUrl(path: string): string {
  const separator = path.includes('?') ? '&' : '?';
  return childContextId
    ? `/api/v1${path}${separator}childId=${encodeURIComponent(childContextId)}`
    : `/api/v1${path}`;
}
